/**
 * ============================================================
 * SIGNUP PAGE - User Registration with OTP Modal
 * ============================================================
 * 
 * This component handles new user registration with OTP verification.
 * 
 * Features:
 * - Name fields (first, middle, last)
 * - Email and phone validation
 * - Password strength validation
 * - Password confirmation matching
 * - After signup, shows OTP modal popup (from VerifyOtp.jsx)
 * 
 * API Endpoint: POST /user
 * ============================================================
 */

import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Form, Input, Button, Typography, Flex, Grid, Alert } from "antd";
import loginImg from "../../assets/images/login-img.svg";
import CaptchaWidget from "../../components/Auth/CaptchaWidget";
import { config } from "../../config";

import "../../assets/styles/components_styles/validation.css";
import "../../assets/styles/components_styles/login.css";
import { isValidEmail, isStrongPassword, isValidPhone, hashPassword } from "../../utils/appUtils";
import { VALIDATION_MESSAGES } from "../../utils/constants";
import Header from "../../components/common/Layouts/Header";
import Footer from "../../components/common/Layouts/Footer";
import HelmetWrapper from "../../components/common/Layouts/HelmetWrapper";
import VerifyOtpModal from "../../components/Auth/VerifyOtpModal";
import {
    signupUser,
    selectSignupSuccess,
    resetSignupSuccess,
    selectSignupLoading,
    selectSignupError,
    clearSignupError
} from "../../redux/userSlice";

const { Title, Text } = Typography;
const { useBreakpoint } = Grid;

export default function SignupPage() {
    // ========== REDUX HOOKS ==========
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const screens = useBreakpoint();
    const signupSuccess = useSelector(selectSignupSuccess);
    const signupLoading = useSelector(selectSignupLoading);
    const signupError = useSelector(selectSignupError);

    // ========== LOCAL STATE ==========
    const [errors, setErrors] = useState({});        // Form validation errors
    const [userEmail, setUserEmail] = useState("");  // Store email for OTP
    const [showOtpModal, setShowOtpModal] = useState(false); // OTP modal visibility
    const [formData, setFormData] = useState({
        firstName: "",
        middleName: "",
        lastName: "",
        email: "",
        phoneNumber: "",
        password: "",
        confirmPassword: ""
    });
    const [captchaToken, setCaptchaToken] = useState("");
    const captchaRef = useRef(null);
    const requireCaptcha = !!config.turnstileSiteKey;

    /**
     * AFTER SIGNUP SUCCESS
     * Navigate to login page
     */
    useEffect(() => {
        if (signupSuccess && userEmail) {
            dispatch(resetSignupSuccess());
            navigate("/login", { state: { message: "Email verified successfully! Please login." } });
        }
    }, [signupSuccess, userEmail, dispatch]);

    /**
     * HANDLE OTP VERIFICATION SUCCESS
     * Show OTP modal
     */
    const handleOtpSuccess = async () => {
        setShowOtpModal(false);
        const hashedPassword = await hashPassword(formData.password);
        const submissionData = {
            first_name: formData.firstName,
            middle_name: formData.middleName,
            last_name: formData.lastName,
            email: formData.email,
            mobile: formData.phoneNumber,
            password: hashedPassword,
            user_role: ["1"]
        };
        if (requireCaptcha && captchaToken) submissionData.captchaToken = captchaToken;
        dispatch(signupUser(submissionData));
    };

    /**
     * INPUT CHANGE HANDLER
     */
    const handleChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        });
        if (errors[e.target.name]) {
            setErrors({
                ...errors,
                [e.target.name]: ""
            });
        }
    };

    /**
     * FORM SUBMIT HANDLER
     */
    const handleSubmit = () => {
        if (!formData.firstName.trim()) {
            setErrors({ firstName: VALIDATION_MESSAGES.FIRST_NAME_REQUIRED || "First name is required" });
            return;
        }
        if (!formData.lastName.trim()) {
            setErrors({ lastName: VALIDATION_MESSAGES.LAST_NAME_REQUIRED || "Last name is required" });
            return;
        }
        if (!formData.email || !isValidEmail(formData.email)) {
            setErrors({ email: VALIDATION_MESSAGES.EMAIL_INVALID });
            return;
        }
        if (!formData.phoneNumber) {
            setErrors({ phoneNumber: VALIDATION_MESSAGES.PHONE_REQUIRED });
            return;
        } else if (!isValidPhone(formData.phoneNumber)) {
            setErrors({ phoneNumber: VALIDATION_MESSAGES.PHONE_INVALID });
            return;
        }
        if (!isStrongPassword(formData.password)) {
            setErrors({ password: VALIDATION_MESSAGES.PASSWORD_WEAK });
            return;
        }
        if (formData.password !== formData.confirmPassword) {
            setErrors({ confirmPassword: VALIDATION_MESSAGES.PASSWORDS_DO_NOT_MATCH });
            return;
        }
        if (requireCaptcha && !captchaToken) {
            setErrors({ confirmPassword: "Please complete the verification." });
            return;
        }
        setErrors({});

        try {
            setUserEmail(formData.email);
            setShowOtpModal(true);
        } catch (error) {
            console.error("Hashing failed", error);
        }
    };

    // ========== RENDER ==========
    return (
        <div className="loginPageRoot">
            {/* SEO HELMET WRAPPER */}
            <HelmetWrapper
                title="Pinginvite - The Go-To Platform for Local Events in Kolkata & Dubai" 
                description="Pingvite connects people with local events in Kolkata and Dubai through community focus, affordability, 
                and city-specific visibility for organizers and users." 
                keywords="Top Upcoming Events in Kolkata, Best Live Events in Kolkata, best upcoming events in Kolkata, 
                online events near you in Kolkata,  upcoming events in Kolkata, Events in Kolkata, Top Upcoming Events in Dubai, 
                Best Live Events in Dubai, best upcoming events in Dubai, online events near you in Dubai, Event calendar in Dubai, 
                Event tickets in Dubai, upcoming events in Dubai, Events in Dubai"
            /> 
            {/* HEADER / NAVBAR */}
            <Header isHome={false} />

            {/* MAIN CONTENT */}
            <main className="loginMain container">
                <div className="loginImage">
                    <img src={loginImg} alt="Signup" className="sideImage" />
                </div>

                <div className="loginFormWrap">
                    <div className="loginCard">
                        <div className="loginCardInner">
                            <div className="loginCardHeader">
                                <Title level={3} className="title">
                                    Create your account
                                </Title>
                                <Text className="color-neutral">
                                    Sign up to start posting offers and deals.
                                </Text>
                            </div>
                            {/* LOGIN ERROR - Shows incorrect password or login failed */}
                            {signupError && (
                                <Alert
                                    message={VALIDATION_MESSAGES.IDENTIFIER_EXISTS}
                                    type="error"
                                    showIcon
                                    closable
                                    onClose={() => dispatch(clearSignupError())}
                                    style={{ marginBottom: 16 }}
                                />
                            )}
                            {/* SIGNUP FORM */}
                            <Form name="signup" layout="vertical" onFinish={handleSubmit}>
                                <Flex gap="small" justify="space-between" wrap="wrap">
                                    <Form.Item
                                        required
                                        label="First Name"
                                        validateStatus={errors.firstName ? "error" : ""}
                                        help={errors.firstName}
                                        style={{ marginBottom: 12, width: !screens.md ? '100%' : '32%' }}
                                    >
                                        <Input
                                            name="firstName"
                                            size="large"
                                            value={formData.firstName}
                                            onChange={handleChange}
                                            prefix={<span className="material-symbols-outlined prefix-blue">person</span>}
                                            placeholder="First Name"
                                            className="hasPrefixIconInput"
                                        />
                                    </Form.Item>

                                    <Form.Item
                                        label="Middle Name"
                                        style={{ marginBottom: 12, width: !screens.md ? '100%' : '32%' }}
                                    >
                                        <Input
                                            name="middleName"
                                            size="large"
                                            value={formData.middleName}
                                            onChange={handleChange}
                                            prefix={<span className="material-symbols-outlined prefix-blue">person</span>}
                                            placeholder="Middle Name"
                                            className="hasPrefixIconInput"
                                        />
                                    </Form.Item>

                                    <Form.Item
                                        required
                                        label="Last Name"
                                        validateStatus={errors.lastName ? "error" : ""}
                                        help={errors.lastName}
                                        style={{ marginBottom: 12, width: !screens.md ? '100%' : '32%' }}
                                    >
                                        <Input
                                            name="lastName"
                                            size="large"
                                            value={formData.lastName}
                                            onChange={handleChange}
                                            prefix={<span className="material-symbols-outlined prefix-blue">person</span>}
                                            placeholder="Last Name"
                                            className="hasPrefixIconInput"
                                        />
                                    </Form.Item>
                                </Flex>

                                <Form.Item
                                    required
                                    label="Email ID"
                                    validateStatus={errors.email ? "error" : ""}
                                    help={errors.email}
                                    style={{ marginBottom: 12 }}
                                >
                                    <Input
                                        name="email"
                                        size="large"
                                        value={formData.email}
                                        onChange={handleChange}
                                        prefix={<span className="material-symbols-outlined prefix-blue">mail</span>}
                                        placeholder="you@example.com"
                                        className="hasPrefixIconInput"
                                    />
                                </Form.Item>

                                <Form.Item
                                    required
                                    label="Phone Number"
                                    validateStatus={errors.phoneNumber ? "error" : ""}
                                    help={errors.phoneNumber}
                                    style={{ marginBottom: 12 }}
                                >
                                    <Input
                                        name="phoneNumber"
                                        size="large"
                                        value={formData.phoneNumber}
                                        onChange={handleChange}
                                        prefix={<span className="material-symbols-outlined prefix-blue">call</span>}
                                        placeholder="Phone Number"
                                        className="hasPrefixIconInput"
                                    />
                                </Form.Item>

                                <Flex gap="small" justify="space-between" wrap="wrap">
                                    <Form.Item
                                        required
                                        label="Password"
                                        validateStatus={errors.password ? "error" : ""}
                                        help={null}
                                        style={{ marginBottom: 12, width: !screens.md ? '100%' : '48%' }}
                                    >
                                        <Input.Password
                                            name="password"
                                            size="large"
                                            value={formData.password}
                                            onChange={handleChange}
                                            prefix={<span className="material-symbols-outlined prefix-blue">lock</span>}
                                            placeholder="Password"
                                            className="hasPrefixIconInput"
                                        />
                                    </Form.Item>

                                    <Form.Item
                                        required
                                        label="Confirm Password"
                                        validateStatus={errors.confirmPassword ? "error" : ""}
                                        help={errors.confirmPassword}
                                        style={{ marginBottom: 12, width: !screens.md ? '100%' : '48%' }}
                                    >
                                        <Input.Password
                                            name="confirmPassword"
                                            size="large"
                                            value={formData.confirmPassword}
                                            onChange={handleChange}
                                            prefix={<span className="material-symbols-outlined prefix-blue">lock</span>}
                                            placeholder="Confirm"
                                            className="hasPrefixIconInput"
                                        />
                                    </Form.Item>

                                    {errors.password && (
                                        <div style={{ width: '100%', marginTop: -5, marginBottom: 10 }}>
                                            <Text type="danger" style={{ fontSize: '12px' }}>
                                                {errors.password}
                                            </Text>
                                        </div>
                                    )}
                                </Flex>

                                <CaptchaWidget ref={captchaRef} onToken={setCaptchaToken} />

                                <Form.Item style={{ marginTop: 8, marginBottom: 12 }}>
                                    <Button
                                        type="primary"
                                        htmlType="submit"
                                        block
                                        size="large"
                                        className="btnGradient"
                                        loading={signupLoading}
                                    >
                                        {signupLoading ? 'Creating Account...' : 'Sign Up'}
                                    </Button>
                                </Form.Item>

                                <Flex align="center" justify="center">
                                    <Text>Already have an account? </Text>
                                    <Link to="/login" style={{ marginLeft: 5 }}>
                                        Sign in
                                    </Link>
                                </Flex>
                            </Form>
                        </div>
                    </div>
                </div>
            </main>

            {/* FOOTER */}
            <Footer />

            {/* OTP VERIFICATION MODAL - Imported from VerifyOtp.jsx */}
            <VerifyOtpModal
                open={showOtpModal}
                onClose={() => setShowOtpModal(false)}
                email={userEmail}
                otpType="signup"
                onSuccess={handleOtpSuccess}
            />
        </div>
    );
}